import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { RootState } from "../../app/store";
import { ConversationType } from "../../redux/chat/chatSlice";

type INTERNAL_KEY_TYPE = "created" | "mission_id" | "author" | "conversation" | "text";

const CONV_KEYS = ["date", "mission", "username", "channel", "text"];
const INTERNAL_KEYS: {[key: string]: INTERNAL_KEY_TYPE} = {
    "date": "created",
    "mission": "mission_id",
    "username": "author",
    "channel": "conversation",
    "text": "text"
};

const id2name = (conversations: ConversationType[], id: string) => {
    const conv = conversations.filter(conversation => conversation.id === id);
    return conv[0]?.name;
};

const sameChannel = (channel1: any, channel2: any) => channel1.id === channel2.id;

export default function ExportDialog({ open, setOpen }: { open: boolean, setOpen: any }) {

    const mission = useSelector((state: RootState) => state.mission.active);
    const allMessages = useSelector((state: RootState) => state.chat.messages);
    const conversations = useSelector((state: RootState) => state.chat.conversations.list);

    const [exportChannels, setExportChannels] = useState<ConversationType[]>([]);
    const [exportFormat, setExportFormat] = useState<string>("csv");
    const [exportFields, setExportFields] = useState<string[]>(["text"]);

    const handleExportChannelsChange = useCallback((event: React.SyntheticEvent<Element, Event>, values: any[]) => {
        setExportChannels(values);
    }, [setExportChannels]);

    const handleExportFormatChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setExportFormat(event.target.value);
    }, [setExportFormat]);

    const handleExportFieldsChange = useCallback((event: React.SyntheticEvent<Element, Event>, values: any[]) => {
        setExportFields([...values.filter(value => value !== "text"), "text"]); // moving "text" to last position
    }, [setExportFields]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const handleExport = useCallback(() => {
        const messages = allMessages.filter(m => exportChannels.filter(channel => channel.id === m.conversation).length);

        if (exportFormat === "json") {
            const url = window.URL.createObjectURL(new Blob([JSON.stringify(messages, null, 4)]));
            const a = document.createElement("a");
            a.href = url;
            a.download = mission + "-conversations.json";
            a.click();

        } else if (exportFormat === "csv") {
            let content = "";

            // column names
            exportFields.forEach((key, index) => {
                if (index !== 0)
                    content += ",";
                content += key;
            });
            content += "\n";

            // data rows
            exportChannels.forEach(channel => {
                const msgs = messages.filter(m => m.conversation === channel.id);
                msgs.forEach(message => {
                    exportFields.forEach((key, index) => {
                        if (index !== 0)
                            content += ",";
                        if (key === "channel")
                            content += id2name(conversations, message[INTERNAL_KEYS[key]]);
                        else
                            content += message[INTERNAL_KEYS[key]];
                    });
                    content += "\n"
                });
            });

            // export
            const url = window.URL.createObjectURL(new Blob([content]));
            const a = document.createElement("a");
            a.href = url;
            a.download = mission + "-conversations.csv";
            a.click();
        }

        handleClose();
    }, [conversations, exportChannels, exportFields, exportFormat, mission, handleClose]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between">
                    Export Chat
                    <FormControl>
                        <RadioGroup row value={null} onChange={handleExportFormatChange}>
                            <FormControlLabel
                                value="csv"
                                control={<Radio size="small" color="secondary" checked={exportFormat === "csv"}/>}
                                label="CSV"
                            />
                            <FormControlLabel
                                value="json"
                                control={<Radio size="small" color="secondary" checked={exportFormat === "json"}/>}
                                label="JSON"
                            />
                        </RadioGroup>
                    </FormControl>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={2}>
                    <Autocomplete
                        fullWidth
                        multiple
                        options={conversations.filter(conversation => conversation.id !== "ALL")}
                        filterSelectedOptions
                        isOptionEqualToValue={sameChannel}
                        value={exportChannels}
                        onChange={handleExportChannelsChange}
                        getOptionLabel={(option: any) => option.name}
                        renderTags={(value: any[], getTagProps) =>
                            value.map((option: any, index: number) => (
                                <Chip variant="outlined" size="small" label={option.name} {...getTagProps({ index })} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Channels"
                                color="secondary"
                            />
                        )}
                        sx={{paddingTop: 1}} // to prevent the TextField's label from being cut by the DialogTitle
                    />
                    {exportFormat === "csv" &&
                        <Autocomplete
                            fullWidth
                            multiple
                            options={CONV_KEYS}
                            filterSelectedOptions
                            value={exportFields}
                            onChange={handleExportFieldsChange}
                            renderTags={(value: any[], getTagProps) =>
                                value.map((option: any, index: number) => (
                                    <Chip
                                        variant="outlined"
                                        size="small"
                                        label={option}
                                        {...(option === "text" ? {key: "text"} : getTagProps({ index }))}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Fields"
                                    color="secondary"
                                />
                            )}
                        />
                    }
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    disabled={false}
                    onClick={handleExport}
                >
                    Export
                </Button>
                <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    onClick={handleClose}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};